import { opposite, step } from "./direction";
import { EPSILON, hop, onCentre, tileOf } from "./motion";
import type { Maze } from "./maze";
import type { Direction, Tile } from "./types";

/**
 * The astronaut.
 *
 * Steering is buffered rather than immediate: a direction the player asks for
 * is remembered and applied at the first tile where it becomes legal. Without
 * that, cornering at speed needs frame-perfect input and the game feels broken.
 */
export class Player {
  x: number;
  y: number;
  dir: Direction;
  /** The turn the player has asked for but that is not yet legal. */
  queued: Direction | null = null;
  /** True while the astronaut is pressed against a wall. */
  blocked = false;

  readonly maze: Maze;

  constructor(maze: Maze, spawn: Tile, dir: Direction = "left") {
    this.maze = maze;
    this.x = spawn.x;
    this.y = spawn.y;
    this.dir = dir;
  }

  get tile(): Tile {
    return tileOf(this);
  }

  reset(spawn: Tile, dir: Direction = "left"): void {
    this.x = spawn.x;
    this.y = spawn.y;
    this.dir = dir;
    this.queued = null;
    this.blocked = false;
  }

  /** Register an intent. It is applied as soon as the maze allows it. */
  steer(dir: Direction): void {
    // A straight reversal is always legal, so take it immediately -- waiting
    // for the next tile centre would make the astronaut overshoot.
    if (dir === opposite(this.dir) && this.maze.isOpen(step(this.tile, dir), false)) {
      this.dir = dir;
      this.queued = null;
      return;
    }
    this.queued = dir;
  }

  /** Move `distance` tiles, turning where the queued direction becomes legal. */
  advance(distance: number): void {
    let remaining = distance;
    let guard = 0;
    while (remaining > EPSILON) {
      if (guard++ > 512) throw new Error("player failed to converge while moving");

      if (onCentre(this)) {
        const tile = this.tile;
        if (this.queued !== null && this.maze.isOpen(step(tile, this.queued), false)) {
          this.dir = this.queued;
          this.queued = null;
        }
        if (!this.maze.isOpen(step(tile, this.dir), false)) {
          this.blocked = true;
          return; // nose to the bulkhead; hold position
        }
      }
      this.blocked = false;

      const { moved } = hop(this, remaining);
      remaining -= moved;
    }
  }
}
